// Environment configuration — values injected at build time via Vite
// For local dev, create a .env.local file in packages/frontend/

/** Entra ID (Azure AD) client ID for the frontend SPA app registration */
export const MSAL_CLIENT_ID = import.meta.env.VITE_MSAL_CLIENT_ID as string;

/** Entra ID authority URL (e.g. https://login.microsoftonline.com/{tenantId}) */
export const MSAL_AUTHORITY = import.meta.env.VITE_MSAL_AUTHORITY as string;

/**
 * Redirect URI after login.
 * Always derived from the current origin so it works on localhost, SWA, and
 * any future custom domain without a rebuild.
 */
export const MSAL_REDIRECT_URI = `${window.location.origin}/auth/callback`;

/** Backend API scope exposed by the backend app registration */
export const BACKEND_API_SCOPE = import.meta.env.VITE_BACKEND_API_SCOPE as string; // e.g. api://<backend-app-id>/excalidraw.access

/**
 * Backend API base URL — empty string means same origin.
 * With SWA Linked Backend, all traffic (/api/*, /collab WSS) routes through
 * the SWA domain to the backend Container App. No separate URL needed.
 */
export const BACKEND_API_BASE = (import.meta.env.VITE_BACKEND_API_BASE as string) || "";

/** Teams channel offered as an alternate feedback and support path. */
export const FEEDBACK_TEAMS_CHAT_URL =
  (import.meta.env.VITE_FEEDBACK_TEAMS_CHAT_URL as string) ||
  "https://teams.cloud.microsoft/l/channel/19%3ABbxvMMnPvyA0DKlHIbibrUb2IvjB-zilWybePfHcCMo1%40thread.tacv2/Excalidraw-MS?groupId=d76488f8-1cc8-4884-9547-1064d51631aa&tenantId=72f988bf-86f1-41af-91ab-2d7cd011db47";

/**
 * URL where users join the Excalidraw-MS access group (MyAccount groups).
 *
 * Membership grants the `Excalidraw.User` app role, which is the sole gate for
 * backend access — the backend service principal runs with
 * `Assignment required = Yes`, so unassigned users get no backend token at all
 * and stay in Local-Only mode. See docs/designs/ux-rules.md § 6a.
 *
 * This is the self-service group rather than the CoreIdentity entitlement: the
 * entitlement does not actually provision the role, while group membership is
 * auto-approved and does.
 *
 * Defaults to the production group so the request-access notice is actionable
 * even where `VITE_ACCESS_REQUEST_URL` is not configured. Set the env var to
 * override (e.g. a different tenant); set it to `""` to drop the link and fall
 * back to generic "contact your administrator" copy.
 */
const DEFAULT_ACCESS_REQUEST_URL =
  "https://myaccount.microsoft.com/groups/aef8f9d2-8c46-4a67-b64c-2cc95fe89d12";

/**
 * Accept only absolute http(s) URLs.
 *
 * The value is baked in at build time from our own CI config, so this is not an
 * attacker-controlled input — but a typo or a bad build variable should degrade
 * to the generic "contact your administrator" copy rather than render a
 * `javascript:` or `data:` href.
 */
function safeHttpUrl(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  try {
    const url = new URL(trimmed);
    return url.protocol === "https:" || url.protocol === "http:" ? url.href : "";
  } catch {
    return "";
  }
}

export const ACCESS_REQUEST_URL = safeHttpUrl(
  import.meta.env.VITE_ACCESS_REQUEST_URL === undefined
    ? DEFAULT_ACCESS_REQUEST_URL
    : (import.meta.env.VITE_ACCESS_REQUEST_URL as string),
);
